<template>
  <div class="admin-page">
    <div class="admin-toolbar">
      <p class="admin-page__hint">轮播图建议尺寸 <strong>1536 × 864</strong> 像素（16:9）</p>
      <div class="admin-toolbar__actions">
            <el-select
              v-model="visibleFilter"
              placeholder="展示状态"
              class="filter-select"
              @change="handleFilterChange"
            >
              <el-option label="全部" :value="-1" />
              <el-option label="展示中" :value="1" />
              <el-option label="已隐藏" :value="0" />
            </el-select>
            <el-button type="primary" @click="openCreateDialog">新增轮播</el-button>
            <el-button @click="fetchList">刷新</el-button>
          </div>
        </div>

        <el-table :data="items" v-loading="loading" style="width: 100%">
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column label="预览" width="140">
            <template #default="scope">
              <el-image
                :src="scope.row.imageUrl"
                fit="cover"
                style="width: 120px; height: 68px; border-radius: 4px"
                :preview-src-list="[scope.row.imageUrl]"
                :preview-teleported="true"
                :z-index="5000"
              >
                <template #error>
                  <div class="image-slot">无图</div>
                </template>
              </el-image>
            </template>
          </el-table-column>
          <el-table-column prop="mainTitle" label="主标题" min-width="160" show-overflow-tooltip />
          <el-table-column prop="subtitle" label="副标题" min-width="200" show-overflow-tooltip />
          <el-table-column prop="sortOrder" label="排序" width="80" />
          <el-table-column prop="isVisible" label="展示" width="90">
            <template #default="scope">
              <el-tag :type="scope.row.isVisible === 1 ? 'success' : 'info'">
                {{ scope.row.isVisible === 1 ? '展示' : '隐藏' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button size="small" @click="openEditDialog(scope.row)">编辑</el-button>
              <el-button size="small" type="danger" @click="deleteItem(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <AdminPagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑轮播图' : '新增轮播图'"
      width="640px"
      destroy-on-close
      @closed="imageBroken = false"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="88px">
        <el-alert
          class="resolution-tip"
          type="info"
          :closable="false"
          show-icon
          title="图片规格建议"
        >
          <template #default>
            <p>最佳分辨率：<strong>1536 × 864</strong> 像素（16:9 横图）</p>
            <p>支持 PNG / JPG / WEBP，单张不超过 10MB。尺寸不一致仍可上传，但首页展示可能裁切或模糊。</p>
          </template>
        </el-alert>

        <el-form-item label="主标题" prop="mainTitle">
          <el-input v-model="form.mainTitle" placeholder="轮播主标题" />
        </el-form-item>
        <el-form-item label="副标题" prop="subtitle">
          <el-input v-model="form.subtitle" type="textarea" :rows="2" placeholder="轮播副标题" />
        </el-form-item>
        <el-form-item label="轮播图片" prop="imageUrl">
          <div class="carousel-upload-wrap">
            <p v-if="imageBroken" class="image-broken-tip">图片无法加载，请重新上传</p>
            <el-upload
              class="carousel-uploader"
              action="/api/v1/upload"
              :show-file-list="false"
              :headers="uploadHeaders"
              :data="uploadData"
              accept="image/png,image/jpeg,image/webp"
              :before-upload="beforeImageUpload"
              :on-success="handleImageSuccess"
            >
              <img
                v-if="form.imageUrl && !imageBroken"
                :src="form.imageUrl"
                class="carousel-upload-preview"
                alt="轮播图预览"
                @error="imageBroken = true"
              />
              <div v-else class="carousel-upload-placeholder">
                <el-icon class="carousel-upload-icon"><Plus /></el-icon>
                <span>点击上传</span>
                <span class="carousel-upload-size">1536 × 864 px</span>
              </div>
            </el-upload>
            <el-button v-if="form.imageUrl" size="small" @click="clearImage">清除图片</el-button>
            <el-input
              v-model="form.imageUrl"
              class="image-url-input"
              placeholder="上传后自动填入，也可手动粘贴图片地址"
            />
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sortOrder">
          <el-input-number v-model="form.sortOrder" :min="0" :max="9999" />
          <span class="form-hint">数字越小越靠前</span>
        </el-form-item>
        <el-form-item label="是否展示" prop="isVisible">
          <el-switch
            v-model="form.isVisible"
            :active-value="1"
            :inactive-value="0"
            active-text="展示"
            inactive-text="隐藏"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitForm">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, reactive, onMounted, computed } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import AdminPagination from '@/components/AdminPagination.vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { validatePhotoUpload, adminUploadFormData, uploadResponseToUrl } from '@/utils/photoUpload'

const RECOMMENDED_WIDTH = 1536
const RECOMMENDED_HEIGHT = 864

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
  'Content-Type': 'application/json'
})

const emptyForm = () => ({
  id: 0,
  mainTitle: '',
  subtitle: '',
  imageUrl: '',
  sortOrder: 0,
  isVisible: 1
})

export default {
  name: 'HomeCarouselManagement',
  components: { AdminPagination, Plus },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const saving = ref(false)
    const items = ref([])
    const currentPage = ref(1)
    const pageSize = ref(10)
    const total = ref(0)
    const visibleFilter = ref(-1)
    const dialogVisible = ref(false)
    const isEdit = ref(false)
    const formRef = ref(null)
    const form = reactive(emptyForm())
    const imageBroken = ref(false)

    const uploadHeaders = computed(() => ({
      Authorization: `Bearer ${localStorage.getItem('jwt_token') || ''}`
    }))
    const uploadData = computed(() => adminUploadFormData('carousel'))

    const rules = {
      mainTitle: [{ required: true, message: '请输入主标题', trigger: 'blur' }],
      imageUrl: [{ required: true, message: '请上传轮播图片', trigger: 'change' }]
    }

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const fetchList = async () => {
      if (!checkLogin()) return
      loading.value = true
      try {
        const response = await axios.post(
          '/api/v1/admin/home/carousel/list',
          {
            page: currentPage.value,
            pageSize: pageSize.value,
            isVisible: visibleFilter.value
          },
          { headers: authHeaders() }
        )
        if (response.data.code === 200 && response.data.data) {
          items.value = response.data.data.items || []
          total.value = response.data.data.total || 0
        } else {
          ElMessage.error(response.data.message || '获取列表失败')
        }
      } catch (error) {
        console.error(error)
        ElMessage.error('获取轮播图列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleFilterChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const handleSizeChange = () => {
      currentPage.value = 1
      fetchList()
    }

    const handlePageChange = () => {
      fetchList()
    }

    const resetForm = () => {
      Object.assign(form, emptyForm())
    }

    const openCreateDialog = () => {
      isEdit.value = false
      resetForm()
      imageBroken.value = false
      dialogVisible.value = true
    }

    const openEditDialog = (row) => {
      isEdit.value = true
      imageBroken.value = false
      Object.assign(form, {
        id: row.id,
        mainTitle: row.mainTitle,
        subtitle: row.subtitle,
        imageUrl: row.imageUrl,
        sortOrder: row.sortOrder,
        isVisible: row.isVisible
      })
      dialogVisible.value = true
    }

    const beforeImageUpload = (file) => {
      if (!validatePhotoUpload(file, (msg) => ElMessage.error(msg))) {
        return false
      }
      return new Promise((resolve) => {
        const img = new Image()
        const url = URL.createObjectURL(file)
        img.onload = () => {
          URL.revokeObjectURL(url)
          if (img.width !== RECOMMENDED_WIDTH || img.height !== RECOMMENDED_HEIGHT) {
            ElMessage.warning(
              `建议使用 ${RECOMMENDED_WIDTH} × ${RECOMMENDED_HEIGHT} 像素（当前 ${img.width} × ${img.height}）`
            )
          }
          resolve(true)
        }
        img.onerror = () => {
          URL.revokeObjectURL(url)
          resolve(true)
        }
        img.src = url
      })
    }

    const handleImageSuccess = (response) => {
      if (response.code === 200) {
        form.imageUrl = uploadResponseToUrl(response)
        imageBroken.value = false
        formRef.value?.validateField('imageUrl')
        ElMessage.success('图片上传成功')
      } else {
        ElMessage.error(response.message || '图片上传失败')
      }
    }

    const clearImage = () => {
      form.imageUrl = ''
      imageBroken.value = false
    }

    const submitForm = async () => {
      if (!formRef.value) return
      try {
        await formRef.value.validate()
      } catch {
        return
      }

      saving.value = true
      try {
        const url = isEdit.value
          ? '/api/v1/home/carousel/update'
          : '/api/v1/home/carousel/create'
        const payload = isEdit.value
          ? {
              id: form.id,
              mainTitle: form.mainTitle,
              subtitle: form.subtitle,
              imageUrl: form.imageUrl,
              sortOrder: form.sortOrder,
              isVisible: form.isVisible
            }
          : {
              mainTitle: form.mainTitle,
              subtitle: form.subtitle,
              imageUrl: form.imageUrl,
              sortOrder: form.sortOrder,
              isVisible: form.isVisible
            }

        const response = await axios.post(url, payload, { headers: authHeaders() })
        if (response.data.code === 200) {
          ElMessage.success(isEdit.value ? '更新成功' : '创建成功')
          dialogVisible.value = false
          fetchList()
        } else {
          ElMessage.error(response.data.message || '保存失败')
        }
      } catch (error) {
        console.error(error)
        ElMessage.error(error.response?.data?.message || '保存失败')
      } finally {
        saving.value = false
      }
    }

    const deleteItem = async (row) => {
      try {
        await ElMessageBox.confirm(`确定删除「${row.mainTitle}」吗？`, '删除确认', {
          type: 'warning'
        })
      } catch {
        return
      }

      try {
        const response = await axios.post(
          '/api/v1/home/carousel/delete',
          { id: row.id },
          { headers: authHeaders() }
        )
        if (response.data.code === 200) {
          ElMessage.success('删除成功')
          fetchList()
        } else {
          ElMessage.error(response.data.message || '删除失败')
        }
      } catch (error) {
        console.error(error)
        ElMessage.error('删除失败')
      }
    }

    onMounted(fetchList)

    return {
      loading,
      saving,
      items,
      currentPage,
      pageSize,
      total,
      visibleFilter,
      dialogVisible,
      isEdit,
      formRef,
      form,
      imageBroken,
      uploadHeaders,
      uploadData,
      rules,
      beforeImageUpload,
      handleImageSuccess,
      clearImage,
      fetchList,
      handleFilterChange,
      handleSizeChange,
      handlePageChange,
      openCreateDialog,
      openEditDialog,
      submitForm,
      deleteItem
    }
  }
}
</script>

<style scoped>

.home-carousel-management {
  min-height: 100%;
}
.page-desc strong {
  color: #606266;
}
.image-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 68px;
  background: #f5f7fa;
  color: #909399;
  font-size: 12px;
}
.form-hint {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}
.resolution-tip {
  margin-bottom: 16px;
}
.resolution-tip p {
  margin: 4px 0;
  font-size: 13px;
  line-height: 1.5;
}
.carousel-upload-wrap {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
}
.carousel-uploader {
  width: 100%;
  max-width: 384px;
  aspect-ratio: 16 / 9;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  transition: border-color 0.2s;
}
.carousel-uploader:hover {
  border-color: #409eff;
}
.carousel-upload-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.carousel-upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: #909399;
  font-size: 13px;
}
.carousel-upload-icon {
  font-size: 28px;
}
.carousel-upload-size {
  font-size: 12px;
  color: #606266;
  font-weight: 500;
}
.image-broken-tip {
  color: #e6a23c;
  font-size: 13px;
  margin: 0;
}
.image-url-input {
  width: 100%;
  max-width: 480px;
}

</style>
